/**
 * Ceske popisky a formatovaci helpery detailu zakazky.
 *
 * Ziji v @reos/reports, aby existovaly jednou a pouzival je stejne detail
 * zakazky (web-admin) i generatory reportu. Tenhle modul je jen re-export —
 * krok S3 z nej odstranil `R3_FIXTURE_URL` (cestu ke commitnute fixture),
 * protoze detail uz cte z API (`lib/dd-api.ts`).
 *
 * JEDINA VLASTNI FUNKCE je `humanFactCaption` nize — viz jeji hlavicka.
 */

import { t, type MessageKey } from '@reos/i18n';
import { factCaption as sharedFactCaption } from '@reos/reports';
import { brokerText } from './broker-text.js';

export {
  AI_INVOLVEMENT_CAPTIONS,
  bandVisual,
  type BandVisual,
  CLAIM_TYPE_CAPTIONS,
  DOMAIN_CAPTIONS,
  DOMAIN_ORDER,
  EVIDENCE_KIND_CAPTIONS,
  FACT_CAPTIONS,
  factCaption,
  factRef,
  factValueNote,
  factValueSummary,
  GATE_CAPTIONS,
  humanizeConnectionValue,
  type HumanizedConnection,
  humanizeReasonText,
  humanValueEntries,
  IMPACT_SEVERITY_CAPTIONS,
  nodeLabelById,
  OWNER_CAPTIONS,
  REVIEW_ROLE_CAPTIONS,
  STATUS_BADGE,
  STOP_REASON_CAPTIONS,
  stopKind,
  type StopKind,
  svgDataUri,
  valueEntries,
  visibleFacts,
  factsByDomain,
} from '@reos/reports';

/**
 * Krok D2 — POPISEK FAKTU PRO HLAVNI POHLED: NIKDY TECHNICKY KLIC.
 *
 * Nalez GPT auditu 7. 10. 2026 (§7.1, audit dema): v zalozce „Co víme" stalo
 * misto nazvu `address.ruian.status` — strojovy klic, ktery makleřce nerika
 * nic a klientovi uz vubec ne. `factCaption` ze sdileneho katalogu vraci pro
 * klic bez popisku SAM KLIC (v PDF ho chrani katalog textu), takze fallback
 * musi byt tady, v adminu.
 *
 * Funkce byla do D2 schovana v `case-cockpit.ts` jako `brokerFactCaption` a
 * zalozka „Co víme" ji nepouzivala. Ted je JEDNA a ctou ji obe mista; klic
 * sam zustava v rozbaleni „Technický detail", kde je na miste.
 */
export function humanFactCaption(factKey: string): string {
  const caption = sharedFactCaption(factKey);
  return caption === (factKey.split('#', 1)[0] ?? factKey)
    ? t('webAdmin.caseCockpit.issue.unknownFactCaption', { locale: 'cs' })
    : caption;
}

/**
 * Krok UXP3 (P1-5) — TEXT OTEVRENE OTAZKY MAKLERI, NIKDY JEJI KLIC.
 *
 * Rucni pruchod 10. 10. 2026 nasel na kokpitu zakazky „Odpovězte na otázku:
 * open:unit_identity.ownership_type" a tentyz klic i v dialogu „Doplnit
 * odpoved". `brokerText` strojovy text SPRAVNE zahodi (podtrzitko =
 * `INTERNAL_TOKEN`), ale volajici si jako zalohu dosadil `question.questionKey`
 * — tedy presne to, co filtr vyhodil.
 *
 * Zaloha je proto KATALOG ADMINU nad klicem otazky (`open:<factKey>`), ne klic
 * sam. Sdileny katalog popisku reportu (`@reos/reports/captions.ts`) se
 * NEMENI: jde do klientskeho PDF a je soucasti schvalene zlate sady.
 * Klic, ktery katalog nezna, dostane obecnou vetu — nic se nevymysli.
 */
const OPEN_QUESTION_TEXT_KEY: Readonly<Record<string, MessageKey>> = {
  'unit_identity.ownership_type': 'webAdmin.caseCockpit.issue.openQuestion.unitOwnershipType',
};

export function humanQuestionText(question: {
  readonly text: string | null;
  readonly questionKey: string;
}): string {
  const text = brokerText(question.text, { fallback: '' });
  if (text !== '') return text;
  const factKey = question.questionKey.startsWith('open:')
    ? question.questionKey.slice('open:'.length)
    : question.questionKey;
  const key = OPEN_QUESTION_TEXT_KEY[factKey.split('#', 1)[0] ?? factKey];
  return t(key ?? 'webAdmin.caseCockpit.issue.openQuestion.generic', { locale: 'cs' });
}
